import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Background from '../components/common/Background';
import { brandingUrls, loginCompanyName, loginCopyrightLine, loginFooterBrand, loginFooterIcp, loginProductTitle, loginTaglines } from '../config/branding';
import { setCredentials } from '../slices/authSlice';
import { useLoginMutation } from '../slices/usersApiSlice';
import beachMp4 from '../assets/video/beach.mp4';
import beachWebm from '../assets/video/beach.webm';
import beachImage from '../assets/video/beach.jpg';
import '../assets/scss/theme/_login-page.scss';

const FALLBACK_TAGLINES = ['更好用的企业级管理软件', '经营数据，一屏掌控', '让管理决策有据可依'];

function LoginField({ label, type, value, onChange, placeholder, autoComplete, error, autoFocus, suffix }) {
	const [focused, setFocused] = useState(false);
	const raised = focused || value.length > 0;

	return (
		<div>
			<div className={`login-page__field-shell${focused ? ' login-page__field-shell--focus' : ''}${error ? ' login-page__field-shell--error' : ''}`}>
				<input
					type={type}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					placeholder={raised ? placeholder : ''}
					autoComplete={autoComplete}
					autoFocus={autoFocus}
					onFocus={() => setFocused(true)}
					onBlur={() => setFocused(false)}
					className={`login-page__input${raised ? ' login-page__input--raised' : ''}`}
				/>
				<label className={`login-page__label${raised ? ' login-page__label--up' : ''}${error ? ' login-page__label--error' : focused ? ' login-page__label--focus' : ''}`}>
					{label}
				</label>
				{suffix ? <div className='login-page__suffix'>{suffix}</div> : null}
			</div>
			{error ? <div className='login-page__field-error'>{error}</div> : null}
		</div>
	);
}

const LoginScreen = () => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [validated, setValidated] = useState(false);
	const [formErrors, setFormErrors] = useState({});
	const [serverError, setServerError] = useState('');
	const [tagIdx, setTagIdx] = useState(0);
	const [tagVisible, setTagVisible] = useState(true);

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [login, { isLoading }] = useLoginMutation();
	const { userInfo } = useSelector((state) => state.auth);

	const { search } = useLocation();
	const redirect = new URLSearchParams(search).get('redirect') || '/dashboard';

	const taglines = Array.isArray(loginTaglines) && loginTaglines.length ? loginTaglines : FALLBACK_TAGLINES;

	useEffect(() => {
		if (userInfo) {
			navigate(redirect);
		}
	}, [userInfo, redirect, navigate]);

	useEffect(() => {
		if (taglines.length < 2) return undefined;
		const timer = setInterval(() => {
			setTagVisible(false);
			setTimeout(() => {
				setTagIdx((i) => (i + 1) % taglines.length);
				setTagVisible(true);
			}, 450);
		}, 4500);
		return () => clearInterval(timer);
	}, [taglines.length]);

	const validateFields = (nextEmail = email, nextPassword = password) => {
		const errors = {};
		if (!nextEmail.trim()) {
			errors.email = '请输入邮箱地址';
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail.trim())) {
			errors.email = '邮箱格式不正确';
		}
		if (!nextPassword.trim()) {
			errors.password = '请输入密码';
		}
		setFormErrors(errors);
		setValidated(true);
		return Object.keys(errors).length === 0;
	};

	const submitHandler = async (e) => {
		e.preventDefault();
		setServerError('');
		if (!validateFields(email, password)) return;

		try {
			const res = await login({ email, password }).unwrap();
			dispatch(setCredentials({ ...res }));
			navigate(redirect);
		} catch (err) {
			const message = err?.data?.message || err.error || '登录失败，请稍后重试';
			setServerError(message);
			toast.error(message);
			setValidated(false);
			setFormErrors({});
		}
	};

	return (
		<div className='login-page'>
			<div className='login-page__bg' aria-hidden>
				<Background video={[beachMp4, beachWebm]} image={beachImage} overlay={false} />
			</div>
			<div className='login-page__overlay' aria-hidden />

			<div className='login-page__panel'>
				<div className='login-page__brand'>
					<div className='login-page__logo-wrap'>
						<img src={brandingUrls.login} alt='' className='login-page__logo-img' />
					</div>
					<div className='login-page__title'>{loginProductTitle}</div>
					<div className='login-page__company'>{loginCompanyName || '\u00a0'}</div>
					<div className='login-page__tagline-row'>
						<div className='login-page__tagline-rule login-page__tagline-rule--left' aria-hidden />
						<div className={`login-page__tagline${tagVisible ? '' : ' login-page__tagline--hidden'}`}>{taglines[tagIdx]}</div>
						<div className='login-page__tagline-rule login-page__tagline-rule--right' aria-hidden />
					</div>
				</div>

				<form className='login-page__form' onSubmit={submitHandler} noValidate>
					<LoginField
						label='邮箱'
						type='email'
						value={email}
						onChange={(v) => {
							setEmail(v);
							setServerError('');
							if (validated) validateFields(v, password);
						}}
						placeholder='name@company.com'
						autoComplete='email'
						autoFocus
						error={formErrors.email || ''}
					/>
					<LoginField
						label='密码'
						type={showPassword ? 'text' : 'password'}
						value={password}
						onChange={(v) => {
							setPassword(v);
							setServerError('');
							if (validated) validateFields(email, v);
						}}
						placeholder='••••••••'
						autoComplete='current-password'
						error={formErrors.password || ''}
						suffix={
							<button type='button' className='login-page__eye' tabIndex={-1} aria-label={showPassword ? '隐藏密码' : '显示密码'} onClick={() => setShowPassword((p) => !p)}>
								{showPassword ? (
									<svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round'>
										<path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94' />
										<path d='M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19' />
										<line x1='1' y1='1' x2='23' y2='23' />
									</svg>
								) : (
									<svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round'>
										<path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
										<circle cx='12' cy='12' r='3' />
									</svg>
								)}
							</button>
						}
					/>

					{serverError ? (
						<div className='login-page__server-error'>
							<svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' style={{ flexShrink: 0 }}>
								<circle cx='12' cy='12' r='10' />
								<line x1='12' y1='8' x2='12' y2='12' />
								<line x1='12' y1='16' x2='12.01' y2='16' />
							</svg>
							{serverError}
						</div>
					) : null}

					<button type='submit' className='login-page__submit' disabled={isLoading}>
						{isLoading ? (
							<span className='login-page__submit-loading'>
								<svg className='login-page__spinner' width='13' height='13' viewBox='0 0 24 24' fill='none'>
									<circle cx='12' cy='12' r='10' stroke='var(--ld-spinner-track)' strokeWidth='3' />
									<path d='M12 2a10 10 0 0 1 10 10' stroke='rgba(255,255,255,0.9)' strokeWidth='3' strokeLinecap='round' />
								</svg>
								验证中
							</span>
						) : (
							'登　录'
						)}
					</button>
				</form>

				<div className='login-page__divider' />

				<div className='login-page__support'>
					<span className='login-page__support-muted'>遇到问题？</span>
					<button type='button' className='login-page__support-link'>
						联系 IT 支持
					</button>
				</div>
			</div>

			<footer className='login-page__footer'>
				<span className='login-page__footer-brand'>{loginFooterBrand}</span>
				<span className='login-page__footer-dot'>·</span>
				<span className='login-page__footer-muted'>{loginFooterIcp}</span>
				<span className='login-page__footer-dot'>·</span>
				<span className='login-page__footer-dim'>{loginCopyrightLine()}</span>
			</footer>
		</div>
	);
};

export default LoginScreen;
